import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { Book, Search, Download, RefreshCw, AlertCircle, ChevronDown } from 'lucide-react';
import { FIREBASE_DB } from '../firebase.config';
import { collection, getDocs, query, orderBy, doc, deleteDoc } from 'firebase/firestore';
import AdminLayout from '../layouts/AdminLayout';

const TrainingTypeColors = {
  onboarding: { bg: 'bg-green-100', text: 'text-green-800' },
  compliance: { bg: 'bg-blue-100', text: 'text-blue-800' },
  safety: { bg: 'bg-purple-100', text: 'text-purple-800' }
};

const AdminTrainingPage = () => {
  const [acknowledgments, setAcknowledgments] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [filter, setFilter] = useState('all');
  const [showMobileFilters, setShowMobileFilters] = useState(false);

  useEffect(() => {
    fetchAcknowledgments();
  }, []);

  const fetchAcknowledgments = async () => {
    try {
      setLoading(true);
      setError(null);
      
      const q = query(
        collection(FIREBASE_DB, 'trainingAcknowledgments'),
        orderBy('acknowledgedAt', 'desc')
      );
      
      const querySnapshot = await getDocs(q);
      const ackList = querySnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
        acknowledgedAt: doc.data().acknowledgedAt?.toDate()
      }));
      
      setAcknowledgments(ackList);
    } catch (err) {
      console.error('Error fetching training acknowledgments:', err);
      setError('Failed to load training acknowledgments. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchAcknowledgments();
  };

  const exportToCSV = () => {
    const filteredAcks = acknowledgments.filter(ack => {
      if (filter !== 'all' && ack.trainingType !== filter) return false;
      if (searchTerm && 
          !(ack.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) || 
            ack.displayName?.toLowerCase().includes(searchTerm.toLowerCase()))) {
        return false;
      }
      return true;
    });
    
    const headers = ['User ID', 'Name', 'Email', 'Training Type', 'Acknowledged At', 'User Agent'];
    const csvContent = [
      headers.join(','),
      ...filteredAcks.map(ack => [
        ack.userId,
        `"${ack.displayName || 'Unknown'}"`,
        `"${ack.userEmail || 'Unknown'}"`,
        ack.trainingType,
        ack.acknowledgedAt ? ack.acknowledgedAt.toLocaleString() : 'Unknown',
        `"${ack.userAgent || 'Unknown'}"`
      ].join(','))
    ].join('\n');
    
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `training-acknowledgments-${new Date().toISOString().slice(0, 10)}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleResetAcknowledgment = async (ackId) => {
    if (!window.confirm('Are you sure you want to reset this training acknowledgment?')) return;
    try {
      await deleteDoc(doc(FIREBASE_DB, 'trainingAcknowledgments', ackId));
      fetchAcknowledgments(); // Refresh the list
    } catch (err) {
      alert('Failed to reset acknowledgment. Please try again.');
      console.error(err);
    }
  };

  const filteredAcknowledgments = acknowledgments.filter(ack => {
    if (filter !== 'all' && ack.trainingType !== filter) return false;
    if (searchTerm) {
      return (
        ack.userEmail?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        ack.displayName?.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }
    return true;
  });

  const renderStatCard = (type, count, bgColor, textColor) => (
    <div className={`${bgColor} p-4 rounded-lg shadow-sm`}>
      <div className={`text-sm font-medium ${textColor} mb-1`}>{type} Completions</div>
      <div className={`text-2xl font-bold ${textColor}`}>{count}</div>
    </div>
  );

  return (
    <AdminLayout>
      <Helmet>
        <title>Admin | Training Management</title>
      </Helmet>
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 w-full">
        {/* Page Title */}
        <div className="mb-6">
          <h1 className="text-2xl font-bold text-gray-900">Training Management</h1>
          <p className="text-sm text-gray-500">Monitor and manage employee training acknowledgments</p>
        </div>

        {/* Mobile Filters Toggle */}
        <div className="lg:hidden mb-4">
          <button
            onClick={() => setShowMobileFilters(!showMobileFilters)}
            className="w-full flex items-center justify-between px-4 py-2 bg-white rounded-lg shadow-sm border border-gray-200"
          >
            <span className="text-sm font-medium text-gray-700">Filters</span>
            <ChevronDown className={`w-5 h-5 text-gray-500 transition-transform ${showMobileFilters ? 'transform rotate-180' : ''}`} />
          </button>
        </div>

        {/* Filters Section */}
        <div className={`bg-white rounded-lg shadow-sm mb-6 ${!showMobileFilters && 'hidden lg:block'}`}>
          <div className="p-4">
            <div className="flex flex-col md:flex-row md:items-center md:justify-between space-y-4 md:space-y-0">
              {/* Search and Filter */}
              <div className="flex flex-col sm:flex-row space-y-4 sm:space-y-0 sm:space-x-4 w-full md:w-auto">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-5 text-gray-400" />
                  <input
                    type="text"
                    value={searchTerm}
                    onChange={e => setSearchTerm(e.target.value)}
                    placeholder="Search by name or email"
                    className="pl-10 pr-4 py-2 w-full sm:w-64 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                  />
                </div>
                <select
                  value={filter}
                  onChange={e => setFilter(e.target.value)}
                  className="w-full sm:w-48 py-2 pl-3 pr-10 border border-gray-300 rounded-md shadow-sm focus:ring-primary focus:border-primary sm:text-sm"
                >
                  <option value="all">All Training Types</option>
                  <option value="onboarding">Onboarding</option>
                  <option value="compliance">Compliance</option>
                  <option value="safety">Safety</option>
                </select>
              </div>

              {/* Action Buttons */}
              <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-2 w-full md:w-auto justify-end">
                <button
                  onClick={handleRefresh}
                  className="inline-flex items-center justify-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-primary"
                >
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Refresh
                </button>
                <button
                  onClick={exportToCSV}
                  className="inline-flex items-center justify-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-green-600 hover:bg-green-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-green-500"
                >
                  <Download className="h-4 w-4 mr-2" />
                  Export CSV
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Error Message */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 mb-6">
            <div className="flex">
              <AlertCircle className="h-5 w-5 text-red-500" />
              <p className="ml-3 text-sm text-red-700">{error}</p>
            </div>
          </div>
        )}

        {/* Content */}
        <div className="bg-white shadow rounded-lg overflow-hidden">
          {loading ? (
            <div className="flex justify-center items-center h-64">
              <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
            </div>
          ) : filteredAcknowledgments.length === 0 ? (
            <div className="text-center py-12">
              <Book className="mx-auto h-12 w-12 text-gray-300" />
              <h3 className="mt-2 text-sm font-medium text-gray-900">No training acknowledgments</h3>
              <p className="mt-1 text-sm text-gray-500">
                {searchTerm || filter !== 'all' 
                  ? 'No results match your search criteria. Try adjusting your filters.' 
                  : 'No training acknowledgments have been recorded yet.'}
              </p>
            </div>
          ) : (
            <div className="overflow-x-auto w-full">
              <div className="w-full align-middle">
                <table className="w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Employee
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Training Type
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Acknowledged
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Sections
                      </th>
                      <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Actions
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {filteredAcknowledgments.map((ack) => {
                      const typeColors = TrainingTypeColors[ack.trainingType] || { bg: 'bg-gray-100', text: 'text-gray-800' };
                      return (
                        <tr key={ack.id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div>
                              <div className="text-sm font-medium text-gray-900">{ack.displayName || 'Unknown'}</div>
                              <div className="text-sm text-gray-500">{ack.userEmail}</div>
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeColors.bg} ${typeColors.text}`}>
                              {ack.trainingType.charAt(0).toUpperCase() + ack.trainingType.slice(1)}
                            </span>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ack.acknowledgedAt ? new Date(ack.acknowledgedAt).toLocaleString() : 'Unknown'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            {ack.sections ? (
                              <span className="text-xs text-gray-600">{ack.sections.length} sections</span>
                            ) : 'N/A'}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                            <button
                              onClick={() => handleResetAcknowledgment(ack.id)}
                              className="text-red-600 hover:underline"
                              title="Reset Acknowledgment"
                            >
                              Reset
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>

        {/* Stats Summary */}
        {!loading && acknowledgments.length > 0 && (
          <div className="mt-6 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-4">
            {renderStatCard(
              'Onboarding',
              acknowledgments.filter(ack => ack.trainingType === 'onboarding').length,
              'bg-green-50',
              'text-green-800'
            )}
            {renderStatCard(
              'Compliance',
              acknowledgments.filter(ack => ack.trainingType === 'compliance').length,
              'bg-blue-50',
              'text-blue-800'
            )}
            {renderStatCard(
              'Safety',
              acknowledgments.filter(ack => ack.trainingType === 'safety').length,
              'bg-purple-50',
              'text-purple-800'
            )}
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default AdminTrainingPage;